<script lang="ts" setup>
import { NDatePicker, useMessage } from "naive-ui";
import { ref, watch, nextTick, computed } from "vue";
import { DateTime } from "luxon";
import { formatJSDate } from "../../utils/dates.ts";

const message = useMessage();

const props = withDefaults(
    defineProps<{
        modelValue: number | null | undefined;
        livePreview?: boolean;
        // n-select props
        placeholder?: string;
        inputFormat?: string;
        // text preview props
        previewIncludeYear?: boolean;
        previewIncludeTime?: boolean;
        previewFormat?:
            | "NUMERIC_DATE_FORMAT"
            | "RELATIVE_DATE_FORMAT"
            | "STANDARD_DATE_FORMAT"
            | "TIME_AGO_FORMAT";
        blockPastDates?: boolean;
        utc?: boolean;
        showCopyBtn?: boolean;
        clearable?: boolean;
    }>(),
    {
        livePreview: false,
        placeholder: "",
        inputFormat: "MM/dd/yyyy",
        previewIncludeYear: true,
        previewIncludeTime: false,
        previewFormat: "RELATIVE_DATE_FORMAT",
        blockPastDates: false,
        utc: false,
        showCopyBtn: false,
        clearable: false,
    },
);

const emits = defineEmits<{
    (_e: "update:modelValue", _value: number | null | undefined): void;
    (_e: "clear"): void;
}>();

const localZone = DateTime.local().zone;

function toDisplayValue(val: number | null | undefined) {
    if (val == null) return val;
    if (!props.utc) return val;

    const utcDate = DateTime.fromMillis(val, { zone: "utc" });
    return DateTime.fromObject(
        { year: utcDate.year, month: utcDate.month, day: utcDate.day },
        { zone: localZone },
    ).toMillis();
}

function toEmittedValue(val: number | null | undefined) {
    if (val == null) return val;
    if (!props.utc) return val;

    const localDate = DateTime.fromMillis(val);
    return DateTime.fromObject(
        { year: localDate.year, month: localDate.month, day: localDate.day },
        { zone: "utc" },
    ).toMillis();
}

const localValue = ref(toDisplayValue(props.modelValue));
const isEditing = ref(false);
const datePickerRef = ref<any>(null);

watch(
    () => props.modelValue,
    (val) => {
        localValue.value = toDisplayValue(val);
    },
);

function onUpdateValue(val: any) {
    localValue.value = val;
    emits("update:modelValue", toEmittedValue(val));
}

function enterEditMode() {
    isEditing.value = true;
    nextTick(() => {
        datePickerRef.value?.focus?.();
    });
}

function handleKeydown(e: KeyboardEvent) {
    if (e.key === "Enter") {
        exitEditMode();
    }
}

function exitEditMode() {
    isEditing.value = false;
}

// Helper for disabling past dates
function isDateDisabled(current: number) {
    if (!props.blockPastDates) return false;
    // Only allow today and future dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return current < today.getTime();
}

const isCopyValid = computed(() => {
    return localValue.value != null;
});

const formattedValue = computed(() => {
    if (localValue.value == null) return "";
    return formatJSDate(new Date(localValue.value), {
        include_year: props.previewIncludeYear,
        include_time: props.previewIncludeTime,
    })[props.previewFormat];
});

const formattedValueForCopyBtn = computed(() => {
    if (localValue.value == null) return "";
    return DateTime.fromMillis(localValue.value).toFormat("MM/dd/yyyy");
});

function handleCopyLabel() {
    if (!isCopyValid.value) return;
    navigator.clipboard.writeText(formattedValueForCopyBtn.value);
    message.info("Copied!", {
        duration: 1000,
    });
}

function handleClear() {
    localValue.value = null;
    emits("update:modelValue", null);
    emits("clear");
}
</script>

<template>
    <div class="relative w-full">
        <n-date-picker
            v-if="!livePreview || isEditing"
            ref="datePickerRef"
            v-bind="$attrs"
            :value="localValue"
            type="date"
            :format="inputFormat || 'MM/dd/yyyy'"
            :placeholder="placeholder"
            :is-date-disabled="isDateDisabled"
            :clearable="clearable"
            class="w-full"
            @clear="handleClear"
            @update-value="onUpdateValue"
            @blur="exitEditMode"
            @keydown="handleKeydown"
        />
        <p
            v-else
            class="cursor-pointer capitalize flex items-center px-3 min-h-[34px] rounded-full w-full border border-transparent hover:bg-cream transition-colors duration-150"
            @click="enterEditMode"
        >
            {{ formattedValue }}
        </p>
        <button
            v-if="showCopyBtn"
            :class="[
                'material-symbols-outlined text-gray-300 text-lg absolute top-0.5 right-7 z-10',
                !isCopyValid ? 'cursor-not-allowed opacity-50' : 'active:scale-95',
            ]"
            :disabled="!isCopyValid"
            @click="handleCopyLabel"
        >
            content_copy
        </button>
    </div>
</template>
